import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Eye, Lightbulb, ArrowLeft, CheckCircle2, AlertCircle, HelpCircle, Play, Clock } from "lucide-react";

const DEMO_CLIP = {
  url: "https://aavaa1510.s3.eu-north-1.amazonaws.com/2026-04-15.08-29-50.bd796e6a-b273-45f4-8afa-80f6048fe143.MP4",
  filename: "2026-04-15.08-29-50",
  time: "8:29 AM",
  duration: "1 min 44 sec",
};

const ANALYSIS = {
  clip_summary: "A caregiver interacts with a young child on a play mat. The caregiver attempts to engage the child in a game of catch with a ball and then shows her a book, while the child demonstrates variable and mostly passive engagement.",
  observations: [
    { title: "How interested is the child while playing?", level: "mixed", assessment: "The child's interest appeared to be mixed and mostly passive. When the caregiver attempted to play catch with a ball, the child sat and watched but did not reach for the ball or attempt to throw it. When the caregiver introduced a book, the child lay down on the mat and then looked at it while lying down.", evidence: "00:46-00:53: Child watches caregiver toss ball but does not participate. 01:02: When caregiver says 'We will do puzzle,' child lies down. 01:15: Child lies on side looking at the book.", source: "gemini-2.5-pro" },
    { title: "Does the child follow instructions?", level: "ignored", assessment: "The child appeared to ignore several verbal and gestural instructions. The caregiver asked her to 'Sit down' and to sit in a specific spot, but the child did not comply. The caregiver ultimately had to physically lift and place her.", evidence: "00:12: Caregiver says 'Sit down,' child continues walking. 00:22: Caregiver points to spot, says 'yahan pe baitho,' child remains. 00:33: Caregiver physically lifts child.", source: "gemini-2.5-pro" },
    { title: "How is the child's balance?", level: "somewhat unsteady", assessment: "The child's balance appeared somewhat unsteady while walking and mostly steady while sitting. She was seen taking a few steps with a wide gait and arms out to the side. When sitting on the mat, her posture appeared stable.", evidence: "00:11-00:13: Child walks with what appears to be a wide-based gait. 00:38-01:01: Child sits independently with stable posture.", source: "gemini-2.5-pro" },
    { title: "Fine motor activities?", level: "no fine motor activity visible", assessment: "No clear fine motor activities like puzzle completion or block stacking were observed. A caregiver introduced a puzzle-like book, and the child passively looked at it and touched the page while lying down, but no active manipulation was visible.", evidence: "01:00: Caregiver brings a puzzle-like book. 01:15-01:29: Child lying on mat, looking at book, lightly touching it.", source: "gemini-2.5-pro" },
    { title: "How cheerful and responsive?", level: "moderate", assessment: "The child's affect appeared generally neutral to moderately positive. She was not seen crying or in distress. A smile was visible when she lay down on the mat. Her responsiveness to verbal prompts for play was low.", evidence: "01:03-01:06: Child smiles while lying on her back on the mat.", source: "gemini-2.5-pro" },
    { title: "Is the child wearing glasses?", level: "wearing consistently", assessment: "Child is seen wearing glasses throughout the clip when face is clearly visible. No removal detected.", evidence: "Glasses visible in frames from 08:29:55 through 08:31:28 consistently.", source: "gpt-5.2" },
    { title: "Glasses removed or thrown?", level: "0 removals", assessment: "No observed removal of glasses across the provided frames.", evidence: "Glasses remain on in all frames where the child's face is visible.", source: "gpt-5.2" },
    { title: "Throwing objects?", level: "no", assessment: "No throwing of objects was observed in this clip.", evidence: "", source: "gemini-2.5-pro" },
    { title: "Any aggression?", level: "no aggression seen", assessment: "No behavior that could be interpreted as violent or aggressive was observed.", evidence: "", source: "gemini-2.5-pro" },
    { title: "Caregiver attention", level: "attentive", assessment: "The caregiver appeared attentive, engaging directly with the child for almost the entire duration by trying to initiate play and guide her.", evidence: "00:27-01:30: Caregiver on the floor with child, attempting games.", source: "gemini-2.5-pro" },
    { title: "Caregiver patience", level: "gentle and patient", assessment: "The caregiver appeared gentle and patient. His tone was calm, and when he physically moved the child after she did not follow instructions, he did so gently.", evidence: "00:33-00:37: Caregiver lifts child and gently places her on the mat.", source: "gemini-2.5-pro" },
    { title: "Caregiver phone use", level: "some phone use", assessment: "Caregiver is visibly using a smartphone in the last few frames, looking down at the screen rather than interacting with the child.", evidence: "Phone visible in caregiver's hands at 08:31:22, 08:31:25, 08:31:28.", source: "gpt-5.2" },
    { title: "Caregiver engagement style", level: "actively engaging", assessment: "The caregiver was actively engaging the child throughout most of the clip. He initiated games, used verbal prompts, and physically guided her to participate.", evidence: "00:27: Caregiver sits on floor to play ball. 01:00: Brings book for a different activity.", source: "gemini-2.5-pro" },
  ],
  suggestions: [
    "The child appeared to respond better when the caregiver was at her level on the floor. Try maintaining face-to-face, floor-level interaction during guided play.",
    "Simple one-step instructions like 'sit here' were ignored — try pairing verbal instructions with a gentle physical prompt from the start, rather than repeating verbal-only commands.",
  ],
  notable_moments: [
    { time: "0:12", desc: "Caregiver says 'Sit down' — child does not follow" },
    { time: "0:33", desc: "Caregiver physically repositions child on the mat" },
    { time: "1:02", desc: "Child lies down when caregiver suggests puzzle" },
    { time: "1:03", desc: "Child smiles while lying on her back" },
  ],
};

const getLevelColor = (level) => {
  if (!level) return "bg-slate-50 text-slate-500";
  const l = level.toLowerCase();
  if (l.includes("wearing") || l === "attentive" || l.includes("gentle") || l.includes("active") || l.includes("no aggression") || l.includes("no phone") || l === "0 removals" || l === "no") return "bg-emerald-50 text-emerald-700 border border-emerald-100";
  if (l.includes("moderate") || l.includes("mixed") || l.includes("somewhat") || l.includes("some")) return "bg-amber-50 text-amber-700 border border-amber-100";
  if (l.includes("low") || l.includes("ignored") || l.includes("resistant") || l.includes("likely") || l.includes("significant")) return "bg-rose-50 text-rose-700 border border-rose-100";
  if (l.includes("not applicable") || l.includes("not enough")) return "bg-slate-50 text-slate-500 border border-slate-100";
  return "bg-[#F7F5F0] text-[#6B7264] border border-[#E2DFD8]";
};

const getLevelIcon = (level) => {
  if (!level) return <HelpCircle size={13} />;
  const l = level.toLowerCase();
  if (l.includes("wearing") || l === "attentive" || l.includes("gentle") || l.includes("active") || l.includes("no aggression") || l.includes("no phone") || l === "0 removals" || l === "no") return <CheckCircle2 size={13} />;
  if (l.includes("ignored") || l.includes("likely")) return <AlertCircle size={13} />;
  return <HelpCircle size={13} />;
};

export default function ClipAnalysisDemo({ onBack }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => { setAnalyzing(false); setShowAnalysis(true); }, 3500);
  };

  return (
    <div className="p-4 pt-6 animate-fade-up" data-testid="clip-analysis-demo">
      <button onClick={onBack} className="flex items-center gap-1 text-[#6B7264] text-sm mb-4 hover:text-[#8E9B82] transition-colors" data-testid="back-to-summary">
        <ArrowLeft size={16} /> Back to Summary
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={20} className="text-[#D4A373]" />
        <h2 className="font-outfit text-2xl font-medium text-[#2F3630]">Clip Analysis</h2>
      </div>
      <p className="font-manrope text-sm text-[#6B7264] mb-5">See how Aavaa understands a single camera clip</p>

      {/* Video Player */}
      <div className="rounded-[20px] overflow-hidden mb-5 shadow-lg border border-[#E2DFD8]/30">
        <video src={DEMO_CLIP.url} controls className="w-full max-h-[260px] bg-black" data-testid="demo-video" poster=""
        ref={(el) => { if (el) { el.currentTime = 40; el.playbackRate = 2; } }}
      />
        <div className="bg-gradient-to-r from-[#2F3630] to-[#3D4A3F] px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={12} className="text-[#D4A373]" />
            <p className="font-manrope text-xs text-white/80">{DEMO_CLIP.time}</p>
            <span className="text-white/30">|</span>
            <p className="font-manrope text-xs text-white/60">{DEMO_CLIP.duration}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] text-white/50 font-manrope">Camera clip</span>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      {!showAnalysis && !analyzing && (
        <Button data-testid="generate-clip-analysis-btn" onClick={runAnalysis} className="w-full rounded-full bg-gradient-to-r from-[#8E9B82] to-[#7A8A6E] hover:from-[#7A8A6E] hover:to-[#6B7B5F] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95 shadow-md shadow-[#8E9B82]/20 mb-6">
          <Sparkles size={18} className="mr-2" /> Generate Analysis
        </Button>
      )}

      {/* Analyzing Animation */}
      {analyzing && (
        <div className="text-center py-10 mb-6">
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border-2 border-[#E2DFD8] opacity-30" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#8E9B82] border-r-[#D4A373] animate-spin" />
            <div className="absolute inset-2 rounded-full border border-[#E2DFD8] opacity-20" />
            <div className="absolute inset-2 rounded-full border border-transparent border-b-[#D4A373] animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}} />
            <Sparkles size={22} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#D4A373]" />
          </div>
          <p className="font-outfit text-base font-medium text-[#2F3630] mb-1">Aavaa is watching this clip...</p>
          <p className="font-manrope text-xs text-[#6B7264]">Understanding scenes, tracking observations, noting moments</p>
        </div>
      )}

      {/* Analysis Result */}
      {showAnalysis && (
        <div className="space-y-5 animate-fade-up">
          {/* Summary Card */}
          <div className="bg-white rounded-[20px] p-5 border border-[#E2DFD8]/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#8E9B82]/15 to-[#D4A373]/15 flex items-center justify-center">
                <Play size={14} className="text-[#8E9B82] ml-0.5" />
              </div>
              <h3 className="font-outfit text-base font-semibold text-[#2F3630]">What happened in this clip</h3>
            </div>
            <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">{ANALYSIS.clip_summary}</p>
          </div>

          {/* Notable Moments */}
          <div className="bg-white rounded-[20px] p-5 border border-[#E2DFD8]/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-[#D4A373]" />
              <h3 className="font-outfit text-base font-semibold text-[#2F3630]">Key Moments</h3>
            </div>
            <div className="space-y-0">
              {ANALYSIS.notable_moments.map((m, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full bg-[#D4A373] mt-1.5" />
                    {i < ANALYSIS.notable_moments.length - 1 && <div className="w-0.5 flex-1 bg-[#E2DFD8] my-1" />}
                  </div>
                  <div className="pb-3.5">
                    <span className="text-[11px] font-manrope font-semibold text-[#D4A373]">{m.time}</span>
                    <p className="font-manrope text-sm text-[#2F3630]">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Observation Cards */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Eye size={18} className="text-[#8E9B82]" strokeWidth={1.5} />
              <h3 className="font-outfit text-base font-semibold text-[#2F3630]">Observation Indicators</h3>
            </div>
            <div className="space-y-2.5">
              {ANALYSIS.observations.map((obs, i) => (
                <div key={i} className="bg-white rounded-[16px] p-4 border border-[#E2DFD8]/50 shadow-sm" data-testid={`clip-obs-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-manrope text-[13px] font-semibold text-[#2F3630] flex-1 leading-snug">{obs.title}</h4>
                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-manrope font-semibold flex-shrink-0 flex items-center gap-1 ${getLevelColor(obs.level)}`}>
                      {getLevelIcon(obs.level)} {obs.level}
                    </span>
                  </div>
                  <p className="font-manrope text-xs text-[#6B7264] leading-relaxed">{obs.assessment}</p>
                  {obs.evidence && (
                    <p className="font-manrope text-[11px] text-[#8E9B82] mt-2 leading-relaxed bg-[#8E9B82]/5 rounded-lg px-3 py-2">
                      {obs.evidence}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${obs.source.includes("gemini") ? "bg-blue-400" : "bg-violet-400"}`} />
                    <span className="text-[9px] text-[#6B7264]/50 font-manrope">{obs.source === "gemini-2.5-pro" ? "Gemini 2.5 Pro (video)" : "GPT-5.2 (frames)"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          <div className="bg-gradient-to-br from-[#D4A373]/8 via-white to-[#8E9B82]/8 rounded-[20px] p-5 border border-[#D4A373]/15 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={18} className="text-[#D4A373]" strokeWidth={1.5} />
              <h3 className="font-outfit text-base font-semibold text-[#2F3630]">Suggestions</h3>
            </div>
            <div className="space-y-3">
              {ANALYSIS.suggestions.map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#D4A373]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-outfit font-bold text-[#D4A373]">{i + 1}</span>
                  </div>
                  <p className="font-manrope text-sm text-[#2F3630] leading-relaxed">{s}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Analysis Sources */}
          <div className="flex items-center justify-center gap-4 py-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-[10px] text-[#6B7264]/60 font-manrope">Gemini 2.5 Pro (video)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-violet-400" />
              <span className="text-[10px] text-[#6B7264]/60 font-manrope">GPT-5.2 (frame analysis)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
